import clsx from 'clsx';
import { CounterPattern } from 'features/ImportData/models';
import { COUPON_CLASSIFICATIONS } from 'features/MenuList/constants';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FaSort, FaSortDown, FaSortUp } from 'react-icons/fa';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList } from 'react-window';
import { DATE_FORMAT_YYYY_MM_DD, getScrollbarWidth } from 'utils';
import { MenuCounterPattern, MenuDetailCounterPattern } from '../models';
import { Columns, SortType } from '../pages/CounterPatternSettingPage';

interface CounterPatternSettingTableProps {
  counterPatternSource: CounterPattern[];
  menuCounterPatternList: MenuCounterPattern[];
  sortBy: { sortName: string; sortType: SortType | null };
  onSortByCol?: (col: Columns) => void;
  onShowCounterPatternMobileOrderDisplayModal?: (
    menu: MenuCounterPattern,
    menuDetail: MenuDetailCounterPattern,
    counterPattern: CounterPattern,
    mobileOrderDisplayFlg: boolean
  ) => void;
  onShowCounterPatternStoreMobileOrderDisplayModal?: (
    menu: MenuCounterPattern,
    menuDetail: MenuDetailCounterPattern
  ) => void;
}

const ROW_HEIGHT = 42;
const MIN_ROW_HEIGHT_TARGET_DATE = 48;
const COLUMN_WIDTH = [97, 140, 147, 85, 500];
const SUBPOS_COLUMN_WIDTH = [204, 108, 187];
const COUNTER_PATTERN_COLUMN_WIDTH = 120;
const LAST_COLUMN_WIDTH = 110;

function CounterPatternSettingTable({
  menuCounterPatternList,
  counterPatternSource,
  sortBy,
  onSortByCol,
  onShowCounterPatternMobileOrderDisplayModal,
  onShowCounterPatternStoreMobileOrderDisplayModal,
}: CounterPatternSettingTableProps) {
  const { t } = useTranslation();

  // refs
  const listRef = useRef<VariableSizeList>(null);
  const headRef = useRef<HTMLDivElement>(null);

  // state
  const [hasVerticalScroll, setHasVerticalScroll] = useState(true);
  const [resetDone, setResetDone] = useState(false);

  // canculate width of table
  const tableWidth =
    COLUMN_WIDTH.reduce((a, b) => a + b, 0) +
    counterPatternSource.length * COUNTER_PATTERN_COLUMN_WIDTH +
    LAST_COLUMN_WIDTH;

  // canculate size of table
  const rowHeights: number[] = menuCounterPatternList.map((menuCounterPattern) => {
    let height = ROW_HEIGHT + 1;
    if (menuCounterPattern.menu_details.length > 0) {
      height = menuCounterPattern.menu_details.length * ROW_HEIGHT + 1;
    }
    return height;
  });

  // get scrollbarWidth
  const scrollbarWidth = getScrollbarWidth();

  // detect divide line between menu_classification
  let currentMenuClassificationId = 0;
  const divideIndexList: number[] = [];
  if (menuCounterPatternList.length > 0) {
    currentMenuClassificationId = menuCounterPatternList[0].menu_classification_id;
    menuCounterPatternList.forEach((menuCounterPattern, index) => {
      if (menuCounterPattern.menu_classification_id !== currentMenuClassificationId) {
        divideIndexList.push(index);
        currentMenuClassificationId = menuCounterPattern.menu_classification_id;
      }
    });
  }

  const getItemSize = (index: number) => {
    return Math.max(rowHeights[index] - 1, MIN_ROW_HEIGHT_TARGET_DATE);
  };

  useEffect(() => {
    if (listRef.current) {
      const listElement = (listRef.current as any)._outerRef as HTMLElement | null;
      if (listElement) {
        const hasVerticalScrollbar = listElement.scrollHeight > listElement.clientHeight;
        setHasVerticalScroll(hasVerticalScrollbar);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listRef.current]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.resetAfterIndex(0, true);
      setResetDone(true);
    }
  }, [menuCounterPatternList]);

  useEffect(() => {
    if (resetDone) {
      if (listRef.current) {
        const listElement = (listRef.current as any)._outerRef as HTMLElement | null;
        if (listElement) {
          listElement.scrollLeft = 0;
          const hasVerticalScrollbar = listElement.scrollHeight > listElement.clientHeight;
          setHasVerticalScroll(hasVerticalScrollbar);
        }
      }
      setResetDone(false);
    }
  }, [resetDone]);

  useEffect(() => {
    const handleScroll = () => {
      if (headRef.current && listRef.current) {
        const listElement = (listRefCurrent as any)._outerRef as HTMLElement | null;
        if (listElement) {
          headRef.current.scrollLeft = listElement.scrollLeft;
        }
      }
    };

    const listRefCurrent = listRef.current;

    if (listRefCurrent) {
      const listElement = (listRefCurrent as any)._outerRef as HTMLElement | null;
      if (listElement) {
        listElement.addEventListener('scroll', handleScroll);
      }
    }

    return () => {
      if (listRefCurrent) {
        const listElement = (listRefCurrent as any)._outerRef as HTMLElement | null;
        if (listElement) {
          listElement.removeEventListener('scroll', handleScroll);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listRef.current, menuCounterPatternList]);

  function handleShowCounterPatternMobileOrderDisplayModal(
    menu: MenuCounterPattern,
    menuDetail: MenuDetailCounterPattern,
    counterPattern: CounterPattern,
    mobileOrderDisplayFlg: boolean
  ) {
    onShowCounterPatternMobileOrderDisplayModal?.(menu, menuDetail, counterPattern, mobileOrderDisplayFlg);
  }

  function handleShowCounterPatternStoreMobileOrderDisplayModal(
    menu: MenuCounterPattern,
    menuDetail: MenuDetailCounterPattern
  ) {
    onShowCounterPatternStoreMobileOrderDisplayModal?.(menu, menuDetail);
  }

  function renderSortStatus(column: string) {
    if (column === sortBy.sortName) {
      if (sortBy.sortType === SortType.DESC) {
        return <FaSortDown size={16} />;
      } else if (sortBy.sortType === SortType.ASC) {
        return <FaSortUp size={16} />;
      }
    }

    return <FaSort size={16} />;
  }

  return (
    <>
      <div className="d-flex gap-2 align-items-center justify-content-end pb-2">
        <div className="d-flex gap-2">
          <div style={{ width: '30px', height: '20px', border: '2px solid red' }}></div>
          <div>{t('CounterPatternSettingPage.setting_guide')}</div>
        </div>
      </div>
      <div className="d-flex" style={{ background: '#999' }}>
        <div
          ref={headRef}
          className="dan-table border-bottom-0 hidden-scrollbar"
          style={{
            overflow: 'auto',
            background: '#999',
            width: `calc(100% - ${hasVerticalScroll ? scrollbarWidth + 'px' : '0px'})`,
          }}
        >
          <div className="dan-thead" style={{ background: '#36489a', minWidth: tableWidth }}>
            <div className="dan-tr">
              <div
                className="dan-th flex-grow-0 flex-shrink-0 cursor-pointer"
                style={{ width: COLUMN_WIDTH[0], position: 'sticky', left: 0, top: 0 }}
                onClick={onSortByCol?.bind(null, Columns.MENU_CLASSIFICATION_ID)}
              >
                <span>{t('CounterPatternSettingPage.menu_classification')}</span>
                <div style={{ position: 'absolute', right: '5px', bottom: '7px' }}>
                  {renderSortStatus(Columns.MENU_CLASSIFICATION_ID)}
                </div>
              </div>
              <div
                className="dan-th flex-column text-center flex-grow-0 flex-shrink-0 cursor-pointer"
                style={{ width: COLUMN_WIDTH[1], position: 'sticky', left: COLUMN_WIDTH[0], top: 0 }}
                onClick={onSortByCol?.bind(null, Columns.CD)}
              >
                <div>{t('CounterPatternSettingPage.menu_code')}</div>
                <div dangerouslySetInnerHTML={{ __html: t('CounterPatternSettingPage.menu_code_description') }}></div>
                <div style={{ position: 'absolute', right: '5px', bottom: '7px' }}>{renderSortStatus(Columns.CD)}</div>
              </div>
              <div
                className="dan-th flex-grow-0 flex-shrink-0 cursor-pointer"
                style={{
                  width: COLUMN_WIDTH[2],
                  position: 'sticky',
                  left: COLUMN_WIDTH[0] + COLUMN_WIDTH[1],
                  top: 0,
                }}
                onClick={onSortByCol?.bind(null, Columns.NAME)}
              >
                <span>{t('CounterPatternSettingPage.menu_name')}</span>
                <div style={{ position: 'absolute', right: '5px', bottom: '7px' }}>
                  {renderSortStatus(Columns.NAME)}
                </div>
              </div>
              <div
                className="dan-th flex-grow-0 flex-shrink-0 cursor-pointer"
                style={{
                  width: COLUMN_WIDTH[3],
                  position: 'sticky',
                  left: COLUMN_WIDTH[0] + COLUMN_WIDTH[1] + COLUMN_WIDTH[2],
                  top: 0,
                }}
                onClick={onSortByCol?.bind(null, Columns.SUB_FLG)}
              >
                <span>{t('CounterPatternSettingPage.menu_type')}</span>
                <div style={{ position: 'absolute', right: '5px', bottom: '7px' }}>
                  {renderSortStatus(Columns.SUB_FLG)}
                </div>
              </div>
              <div
                className="dan-th d-flex flex-column flex-grow-0 flex-shrink-0 p-0 border-end"
                style={{
                  width: COLUMN_WIDTH[4],
                  position: 'sticky',
                  left: COLUMN_WIDTH[0] + COLUMN_WIDTH[1] + COLUMN_WIDTH[2] + COLUMN_WIDTH[3],
                  top: 0,
                }}
              >
                <div
                  className="text-center d-flex flex-grow-0 flex-shrink-0 align-items-center"
                  style={{ padding: '0.5rem', height: '33px', flexBasis: '33px' }}
                >
                  {t('CounterPatternSettingPage.pos_info')}
                </div>
                <div className="dan-tr nested flex-fill">
                  <div
                    className="dan-th p-0 flex flex-column text-break flex-shrink-0 flex-grow-0"
                    style={{ flexBasis: SUBPOS_COLUMN_WIDTH[0] }}
                  >
                    <div className="dan-tr nested flex-fill">
                      <div
                        className="dan-th border-top-0 text-break flex-shrink-0 flex-grow-0"
                        style={{ flexBasis: '100%' }}
                      >
                        {t('CounterPatternSettingPage.target_date')}
                      </div>
                    </div>
                    <div className="dan-tr nested flex-fill">
                      <div
                        className="dan-th text-break flex-shrink-0 flex-grow-0 position-relative cursor-pointer"
                        style={{ flexBasis: '50%' }}
                        onClick={onSortByCol?.bind(null, Columns.START_DATE)}
                      >
                        <span>{t('CounterPatternSettingPage.start_date')}</span>
                        <div style={{ position: 'absolute', right: '5px', bottom: '7px' }}>
                          {renderSortStatus(Columns.START_DATE)}
                        </div>
                      </div>
                      <div
                        className="dan-th text-break flex-shrink-0 flex-grow-0 position-relative cursor-pointer"
                        style={{ flexBasis: '50%' }}
                        onClick={onSortByCol?.bind(null, Columns.END_DATE)}
                      >
                        <span>{t('CounterPatternSettingPage.end_date')}</span>
                        <div style={{ position: 'absolute', right: '5px', bottom: '7px' }}>
                          {renderSortStatus(Columns.END_DATE)}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    className="dan-th text-break  flex-shrink-0 flex-grow-0"
                    style={{ flexBasis: SUBPOS_COLUMN_WIDTH[1] }}
                  >
                    {t('CounterPatternSettingPage.pos_code')}
                  </div>
                  <div
                    className="dan-th text-break  flex-shrink-0 flex-grow-0 border-end"
                    style={{ flexBasis: SUBPOS_COLUMN_WIDTH[2] }}
                  >
                    {t('CounterPatternSettingPage.pos_name')}
                  </div>
                </div>
              </div>
              <div className="dan-th d-flex flex-column flex-fill p-0">
                <div
                  className="text-center d-flex flex-grow-0 flex-shrink-0 align-items-center"
                  style={{ padding: '0.5rem', height: '33px', flexBasis: '33px' }}
                >
                  {t('CounterPatternSettingPage.display_counter_pattern')}
                </div>
                <div className="dan-tr nested flex-fill">
                  {counterPatternSource.map(function (counterPattern) {
                    return (
                      <div
                        key={counterPattern.id}
                        className="dan-th text-break text-center"
                        style={{ width: COUNTER_PATTERN_COLUMN_WIDTH }}
                      >
                        {counterPattern.cd}&nbsp;{counterPattern.name}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div
                className="dan-th border-end-0 flex-grow-0 flex-shrink-0"
                style={{ width: LAST_COLUMN_WIDTH, position: 'sticky', right: 0, top: 0 }}
              >
                {t('CounterPatternSettingPage.setting_store_search')}
              </div>
            </div>
          </div>
        </div>
        {hasVerticalScroll && <div className="dan-th1 flex-fill" style={{ backgroundColor: '#fff' }}></div>}
      </div>
      <div className="text-center flex-grow-1 flex-shrink-1" style={{ background: '#999' }}>
        {menuCounterPatternList.length > 0 && (
          <AutoSizer>
            {({ height, width }) => (
              <VariableSizeList
                ref={listRef}
                className="menu-counter-pattern-list border-bottom"
                width={width}
                height={height}
                itemSize={getItemSize}
                itemCount={rowHeights.length}
                itemData={menuCounterPatternList}
              >
                {({ style, index }) => {
                  const menu = menuCounterPatternList[index];
                  return (
                    <div key={menu.id} style={{ ...style, minWidth: tableWidth }}>
                      <div
                        className={clsx('dan-tr position-relative', {
                          divide: divideIndexList.includes(index),
                        })}
                      >
                        <div
                          className="dan-td d-flex justify-content-center flex-grow-0 flex-shrink-0"
                          style={{
                            width: COLUMN_WIDTH[0],
                            backgroundColor: '#fff',
                            position: 'sticky',
                            left: 0,
                            zIndex: 10,
                          }}
                        >
                          {menu.menu_classification_name}
                        </div>
                        <div
                          className="dan-td d-flex justify-content-center flex-grow-0 flex-shrink-0"
                          style={{
                            width: COLUMN_WIDTH[1],
                            backgroundColor: '#fff',
                            position: 'sticky',
                            left: COLUMN_WIDTH[0],
                            zIndex: 10,
                          }}
                        >
                          {menu.cd}
                          {COUPON_CLASSIFICATIONS[menu.coupon_classification]
                            ? `${COUPON_CLASSIFICATIONS[menu.coupon_classification]}`
                            : ''}
                        </div>
                        <div
                          className="dan-td d-flex justify-content-center flex-grow-0 flex-shrink-0"
                          style={{
                            width: COLUMN_WIDTH[2],
                            backgroundColor: '#fff',
                            position: 'sticky',
                            left: COLUMN_WIDTH[0] + COLUMN_WIDTH[1],
                            zIndex: 10,
                          }}
                        >
                          {menu.name}
                        </div>
                        <div
                          className="dan-td d-flex justify-content-center flex-grow-0 flex-shrink-0"
                          style={{
                            width: COLUMN_WIDTH[3],
                            backgroundColor: '#fff',
                            position: 'sticky',
                            left: COLUMN_WIDTH[0] + COLUMN_WIDTH[1] + COLUMN_WIDTH[2],
                            zIndex: 10,
                          }}
                        >
                          {menu.sub_flg ? '〇' : 'ー'}
                        </div>
                        <div
                          className="dan-td d-flex justify-content-center flex-grow-0 flex-shrink-0"
                          style={{
                            width: SUBPOS_COLUMN_WIDTH[0] / 2,
                            backgroundColor: '#fff',
                            position: 'sticky',
                            left: COLUMN_WIDTH[0] + COLUMN_WIDTH[1] + COLUMN_WIDTH[2] + COLUMN_WIDTH[3],
                            zIndex: 10,
                            lineHeight: '1.25em',
                          }}
                        >
                          {moment(menu.start_date).format(DATE_FORMAT_YYYY_MM_DD)}
                        </div>
                        <div
                          className="dan-td d-flex justify-content-center flex-grow-0 flex-shrink-0"
                          style={{
                            width: SUBPOS_COLUMN_WIDTH[0] / 2,
                            backgroundColor: '#fff',
                            position: 'sticky',
                            left:
                              COLUMN_WIDTH[0] +
                              COLUMN_WIDTH[1] +
                              COLUMN_WIDTH[2] +
                              COLUMN_WIDTH[3] +
                              SUBPOS_COLUMN_WIDTH[0] / 2,
                            zIndex: 10,
                            lineHeight: '1.25em',
                          }}
                        >
                          {moment(menu.end_date).format(DATE_FORMAT_YYYY_MM_DD)}
                        </div>
                        <div
                          className="dan-td d-flex justify-content-center flex-grow-0 flex-shrink-0 p-0"
                          style={{
                            width: SUBPOS_COLUMN_WIDTH[1],
                            backgroundColor: '#fff',
                            position: 'sticky',
                            left:
                              COLUMN_WIDTH[0] +
                              COLUMN_WIDTH[1] +
                              COLUMN_WIDTH[2] +
                              COLUMN_WIDTH[3] +
                              SUBPOS_COLUMN_WIDTH[0],
                            zIndex: 10,
                          }}
                        >
                          <div className="dan-tr d-flex flex-column" style={{ height: getItemSize(index) }}>
                            {menu.menu_details.map((menuDetail, index) => {
                              const itemHeight = 100 / menu.menu_details.length;
                              let borderClass = '';
                              if (index === 0) {
                                borderClass = 'border-end-0 border-top-0';
                              } else if (index === menu.menu_details.length - 1) {
                                borderClass = 'border-end-0';
                              }

                              return (
                                <div
                                  className={`dan-td justify-content-center border-start-0 ${borderClass}`}
                                  key={menuDetail.menu_id + '_pos_code_' + index}
                                  style={{ flexBasis: `${itemHeight}%` }}
                                >
                                  {menuDetail.pos_code_cd}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                        <div
                          className="dan-td d-flex justify-content-center flex-grow-0 flex-shrink-0 p-0"
                          style={{
                            width: SUBPOS_COLUMN_WIDTH[2],
                            backgroundColor: '#fff',
                            position: 'sticky',
                            left:
                              COLUMN_WIDTH[0] +
                              COLUMN_WIDTH[1] +
                              COLUMN_WIDTH[2] +
                              COLUMN_WIDTH[3] +
                              SUBPOS_COLUMN_WIDTH[0] +
                              +SUBPOS_COLUMN_WIDTH[1],
                            zIndex: 10,
                          }}
                        >
                          <div className="dan-tr d-flex flex-column" style={{ height: getItemSize(index) }}>
                            {menu.menu_details.map((menuDetail, index) => {
                              const itemHeight = 100 / menu.menu_details.length;
                              let borderClass = '';
                              if (index === 0) {
                                borderClass = 'border-top-0';
                              }

                              return (
                                <div
                                  className={`dan-td justify-content-center border-start-0 border-end ${borderClass}`}
                                  key={menuDetail.menu_id + '_name_' + index}
                                  style={{ flexBasis: `${itemHeight}%` }}
                                >
                                  {menuDetail.name}
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {counterPatternSource.map(function (counterPattern) {
                          return (
                            <div
                              key={counterPattern.id}
                              className="dan-td d-flex justify-content-center flex-fill p-0"
                              style={{ width: COUNTER_PATTERN_COLUMN_WIDTH }}
                            >
                              <div className="dan-tr d-flex flex-column" style={{ height: getItemSize(index) }}>
                                {menu.menu_details.map((menuDetail, index) => {
                                  const itemHeight = 100 / menu.menu_details.length;
                                  let borderClass = '';
                                  if (index === 0) {
                                    borderClass = 'border-end-0 border-top-0';
                                  } else if (index === menu.menu_details.length - 1) {
                                    borderClass = 'border-end-0';
                                  }

                                  const moDisplayInfo =
                                    menuDetail.mo_display_by_counter_pattern?.[counterPattern.id.toString()];

                                  const isSettingByUser = moDisplayInfo?.is_change_from_restaurant || false;

                                  if (!moDisplayInfo) {
                                    return (
                                      <div
                                        className={`dan-td justify-content-center p-0 border-start-0 ${borderClass}`}
                                        key={
                                          menuDetail.menu_id +
                                          '_counterpattern_' +
                                          counterPattern.id.toString() +
                                          '_' +
                                          index
                                        }
                                        style={{
                                          background: '#fff',
                                          flexBasis: `${itemHeight}%`,
                                        }}
                                      >
                                        <span
                                          className="not-apply d-inline-block p-1"
                                          data-poscodeid={menuDetail.pos_code_id}
                                        >
                                          {t('StoreSettingDetailPage.not_apply')}
                                        </span>
                                      </div>
                                    );
                                  }

                                  return (
                                    <div
                                      className={`dan-td justify-content-center border-start-0 p-0 ${borderClass}`}
                                      key={
                                        menuDetail.menu_id +
                                        '_counterpattern_' +
                                        counterPattern.id.toString() +
                                        '_' +
                                        index
                                      }
                                      style={{ background: '#fff', flexBasis: `${itemHeight}%` }}
                                    >
                                      <Button
                                        className={clsx({
                                          'is-mo-setting': isSettingByUser,
                                          'btn--dark-green': moDisplayInfo.mobile_order_display_flg,
                                          'btn--green': !moDisplayInfo.mobile_order_display_flg,
                                        })}
                                        data-poscodeid={menuDetail.pos_code_id}
                                        onClick={handleShowCounterPatternMobileOrderDisplayModal.bind(
                                          null,
                                          menu,
                                          menuDetail,
                                          counterPattern,
                                          moDisplayInfo.mobile_order_display_flg
                                        )}
                                      >
                                        {moDisplayInfo.mobile_order_display_flg
                                          ? t('StoreSettingDetailPage.enable')
                                          : t('StoreSettingDetailPage.disable')}
                                      </Button>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}

                        <div
                          className="dan-td d-flex justify-content-center flex-shrink-0 flex-grow-0 p-0"
                          style={{ width: LAST_COLUMN_WIDTH, position: 'sticky', right: 0, background: '#fff' }}
                        >
                          <div className="dan-tr d-flex flex-column" style={{ height: getItemSize(index) }}>
                            {menu.menu_details.map((menuDetail, index) => {
                              const itemHeight = 100 / menu.menu_details.length;
                              let borderClass = '';
                              if (index === 0) {
                                borderClass = 'border-top-0';
                              }

                              return (
                                <div
                                  className={`dan-td justify-content-center border-start-0 border-end-0 ${borderClass}`}
                                  key={menuDetail.menu_id + '_settingstore_' + index}
                                  style={{ flexBasis: `${itemHeight}%` }}
                                >
                                  <Button
                                    onClick={handleShowCounterPatternStoreMobileOrderDisplayModal.bind(
                                      null,
                                      menu,
                                      menuDetail
                                    )}
                                  >
                                    {t('CounterPatternSettingPage.setting_store')}
                                  </Button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }}
              </VariableSizeList>
            )}
          </AutoSizer>
        )}
      </div>
    </>
  );
}

export default React.memo(CounterPatternSettingTable);
